import { PaginatedResponse } from "@medusajs/types"
import { Product } from "modules/products/form/schema"
// eslint-disable-next-line @nx/enforce-module-boundaries
import { EStatus } from "types/common"

export type PromotionListParam = {
  limit?: number
  offset?: number
  vendorId?: string
  q?: string
}

export enum EPromotionCompareType {
  All = "all",
  Include = "include",
  Exclude = "exclude",
}

export enum EPromotionSubtotalType {
  Product = "product",
  Principle = "principle",
}

export type PromotionItem = {
  id: string
  // Basic Information
  name: string // Name of the promotion
  description?: string // Optional description
  priority: number // Lower number = higher priority
  status: EStatus // enum: active, draft, expired

  // Time Constraints (both must be set or both null)
  available_from: Date | string // Start date-time in ISO format
  available_to: Date | string // End date-time in ISO format

  // Promotion Behavior
  stop_other_promotions: boolean // If true, prevents other promotions from applying
  display_in_storefront: boolean // If true, force show promotion in product detail
  next_tier_id?: string // Optional: ID of the next tier promotion
  use_available_period?: boolean
  exclude_invoice_discount_items?: boolean // Promotion table: Ignore subtotal‑promotion exclusions from subtotal

  // Conditions (at least one condition is required)
  // condition_product_price_compare: EDiscountConditionPrice // enum: greater_than, less_than, equal_to
  // condition_product_price_value: number // Price value for comparison
  condition_product_price: {
    id: string
    price: number
    compare_type: EDiscountConditionPrice
  }[]

  condition_categories: {
    id: string
    categories: string[]
    compare_type: EPromotionCompareType
  }[] // Array of category IDs
  condition_products: {
    id: string
    products: Product[]
    compare_type: EPromotionCompareType
  }[] // Array of product IDs
  condition_customers: {
    id: string
    customers: string[]
    compare_type: EPromotionCompareType
  }[] // Array of user IDs
  condition_customer_groups: {
    id: string
    compare_type: EPromotionCompareType
    customer_groups: string[]
  }[] // Array of user group IDs

  condition_order_subtotal: number // Minimum order subtotal
  condition_total_number_of_products: number // Minimum number of products
  condition_product_quantity: number // Quantity of specific product
  condition_product_in_carts: {
    product_id: string
    quantity: number
    id: string
    product: Product
  }[]

  condition_coupon_code: string // Required coupon code
  condition_max_usage: number // Maximum number of uses
  condition_once_per_customer: boolean | null // Limit to one use per customer
  condition_order?: {
    value?: number | null
    exclude_invoice_discount_items?: boolean
    product_condition?: {
      compare_type: EPromotionCompareType
      principle?: string[]
      categories?: string[]
      products?: string[]
    } | null
  } | null

  // Bonus/Rewards (must be properly paired)
  // Order Discount (both fields required if used)
  bonus_order_discount: number // Discount amount
  bonus_order_discount_type: EDiscountType // enum: percentage, fixed

  // Free Product (both fields required if used)
  bonus_free_product_number: number // Number of free products
  bonus_free_product_id: string // ID of the free product

  // Shipping and Points
  bonus_free_shipping: boolean | null // Free shipping flag
  bonus_award_points: number // Loyalty points to award

  // Product-specific Discount (all three required if used)
  bonus_discount_on_products: {
    product_id: string
    discount_amount: number
    discount_type: EDiscountType
    product: Product
  }[] // Products to discount
  bonus_free_products: {
    product_id: string
    quantity: number
    product: Product
  }[] // Products to discount
  bonus_discount_on_product_amount: number // Discount amount
  bonus_discount_on_product_type: EDiscountType // enum: percentage, fixed
}

export enum EDiscountType {
  Percentage = "percentage",
  Fixed = "fixed",
}

export enum EDiscountConditionPrice {
  Equal = "equal",
  NotEqual = "not_equal",
  EqualOrLess = "equal_or_less",
  EqualOrGreater = "equal_or_greater",
  LessThan = "less_than",
  GreaterThan = "greater_than",
}

export type PromotionListResponse = PaginatedResponse<{
  promotions: PromotionItem[]
}>

export type PromotionDetailResponse = {
  data: PromotionItem
}

export type PromotionDetailParam = {
  id: string
  vendorId?: string
}

export type PromotionCreateBody = {
  // Basic Information
  name: string // Name of the promotion
  description?: string // Optional description
  priority?: number | null // Lower number = higher priority
  status: EStatus // enum: active, draft, expired

  // Time Constraints (both must be set or both null)
  available_from: number | null // Start date-time in ISO format
  available_to: number | null // End date-time in ISO format

  // Promotion Behavior
  stop_other_promotions: boolean // If true, prevents other promotions from applying
  display_in_storefront: boolean // If true, force show promotion in product detail
  next_tier_id?: string // Optional: ID of the next tier promotion
  use_available_period: boolean
  exclude_invoice_discount_items?: boolean // Promotion table: Ignore subtotal‑promotion exclusions from subtotal

  // Conditions (at least one condition is required)
  // condition_product_price_compare?: EDiscountConditionPrice | null // enum: greater_than, less_than, equal_to
  // condition_product_price_value?: number | null // Price value for comparison
  condition_product_price?: {
    price?: number | null
    compare_type?: EDiscountConditionPrice | null
  }[]
  condition_categories?:
    | {
        categories: string[]
        compare_type?: EPromotionCompareType
      }[]
    | null // Array of category IDs

  condition_products?:
    | {
        products: string[]
        compare_type?: EPromotionCompareType
      }[]
    | null // Array of product IDs
  condition_customers: {
    customers: string[]
    compare_type?: EPromotionCompareType
  }[] // Array of user IDs
  condition_customer_groups: {
    customer_groups: string[]
    compare_type?: EPromotionCompareType
  }[] // Array of user group IDs

  condition_order_subtotal?: number | null // Minimum order subtotal
  condition_total_number_of_products?: number | null // Minimum number of products
  condition_product_in_carts: { product_id: string; quantity: number }[]

  condition_coupon_code?: string | null // Required coupon code
  condition_max_usage?: number | null // Maximum number of uses
  condition_once_per_customer?: boolean | null // Limit to one use per customer

  // Bonus/Rewards (must be properly paired)
  // Order Discount (both fields required if used)
  bonus_order_discount?: number | null // Discount amount
  bonus_order_discount_type?: EDiscountType | null // enum: percentage, fixed

  // Shipping and Points
  bonus_free_shipping?: boolean | null // Free shipping flag
  bonus_award_points?: number | null // Loyalty points to award

  // Product-specific Discount (all three required if used)
  bonus_discount_on_products: {
    product_id: string
    discount_amount: number
    discount_type: EDiscountType
  }[] // Products to discount
  bonus_free_products: {
    product_id: string
    quantity: number
  }[]

  condition_order?: {
    value?: number | null
    exclude_invoice_discount_items?: boolean
    product_condition?: {
      compare_type: EPromotionCompareType
      principle?: string[]
      categories?: string[]
      products?: string[]
    } | null
  } | null
}
